import { cn } from '@/lib/utils';
import type { OrderSide } from '@/analytics';

export interface LobbyCardRowProps {
  /** Row label — `Yes` / `No`, or a market / option name. */
  label: string;
  /** Probability text, e.g. `23% Yes` — the chip shows the percent, the word feeds the aria-label. */
  percentLabel: string;
  /** Payout multiplier text, e.g. `4.13x`. */
  payoutLabel: string;
  /** Drives the accent colour when `accent` is not given — Yes → brand purple, No → blue. */
  side: OrderSide;
  /**
   * Explicit accent override. Multi-option / multi-market cards alternate
   * purple/blue by row position (Figma 5751-141388: Republican purple,
   * Democrat blue) even though every row is a Yes side.
   */
  accent?: 'purple' | 'blue';
  /** Open the buy popup for this row. */
  onSelect: () => void;
}

/**
 * One lobby-card row in the V2 design (Figma 5751-141388 "Questions Card"):
 * the option label with a thin 4px probability underline beneath it, the
 * payout multiplier, and a filled "subtle" percent chip — purple tint for
 * Yes, blue tint for No.
 *
 * Presentational only — it renders whatever the card's row model provides;
 * the single-vs-multi / option logic is unchanged.
 */
export function LobbyCardRow({ label, percentLabel, payoutLabel, side, accent, onSelect }: LobbyCardRowProps) {
  const [pct, ...rest] = percentLabel.split(' ');
  const word = rest.join(' ');
  const parsed = Number.parseInt(pct, 10);
  const barPct = Number.isFinite(parsed) ? Math.max(0, Math.min(100, parsed)) : 0;
  const tone = accent ?? (side === 'yes' ? 'purple' : 'blue');

  return (
    <div className="flex items-center gap-6 py-1">
      <div className="min-w-0 flex-1">
        <span className="block truncate text-[16px] leading-normal tracking-[-0.32px] text-ink">{label}</span>
        {/* Probability underline — only the coloured fill, no track behind it. */}
        <div
          className={cn('h-[4px] rounded-pill', tone === 'purple' ? 'bg-purple' : 'bg-chip-blue-text')}
          style={{ width: `${barPct}%` }}
        />
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        <span className="text-[14px] tracking-[-0.28px] text-ink-muted">{payoutLabel}</span>
        <button
          type="button"
          aria-label={`${label} — ${pct}${word ? ` ${word}` : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          className={cn(
            'flex h-8 w-[74px] items-center justify-center rounded-pill transition-opacity hover:opacity-80',
            tone === 'purple' ? 'bg-chip-purple text-purple' : 'bg-chip-blue text-chip-blue-text',
          )}
        >
          <span className="text-[16px] font-semibold tracking-[-0.32px]">{pct}</span>
        </button>
      </div>
    </div>
  );
}
